/**
 * Challenge Node Validator
 * Validates Question/Answer, QR Code, and Numeric Input nodes
 */

import { WorkflowNode } from '@store/workflowStore'
import { WorkflowNodeType, QuestionAnswerType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCodes,
} from '@features/adventures/types/validation.types'
import { createIssue } from './helpers'

/**
 * Validate a Question/Answer node
 */
function validateQuestionAnswer(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.questionAnswerConfig

  if (!config) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_QUESTION_TEXT,
      message: 'Question configuration is missing',
    }))
    return issues
  }

  // Question text >= 5 characters
  if (!config.questionText || config.questionText.length < 5) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.QUESTION_TEXT_TOO_SHORT,
      message: 'Question text must be at least 5 characters',
      details: `Current length: ${config.questionText?.length || 0}`,
    }))
  }

  // Validate based on answer type
  if (config.answerType === QuestionAnswerType.FreeText) {
    if (!config.correctAnswer || config.correctAnswer.length < 2) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.ANSWER_TOO_SHORT,
        message: 'Answer must be at least 2 characters for free text questions',
        details: `Current length: ${config.correctAnswer?.length || 0}`,
      }))
    }
  } else if (
    config.answerType === QuestionAnswerType.MultipleChoice ||
    config.answerType === QuestionAnswerType.MultiSelect
  ) {
    // Check that there are answer options
    if (!config.answerOptions || config.answerOptions.length === 0) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.NO_ANSWER_SELECTED,
        message: 'Multiple choice questions must have answer options',
      }))
    } else {
      // Check that at least one answer is marked as correct
      const hasCorrectAnswer = config.answerOptions.some(opt => opt.isCorrect)
      if (!hasCorrectAnswer) {
        issues.push(createIssue({
          nodeId: node.id,
          nodeLabel: node.data.label,
          nodeType: node.data.nodeType,
          severity: ValidationSeverity.Error,
          category: ValidationCategory.Configuration,
          code: ValidationErrorCodes.NO_ANSWER_SELECTED,
          message: 'At least one correct answer must be selected',
        }))
      }
    }
  }

  return issues
}

/**
 * Validate a QR Code Scanner node
 */
function validateQRCode(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.qrCodeConfig

  // Must have customCode OR at least one sharedCode
  const hasCustomCode = !!config?.customCode && config.customCode.trim().length > 0
  const hasSharedCodes = (config?.sharedCodes?.length || 0) > 0

  if (!hasCustomCode && !hasSharedCodes) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_QR_CODE,
      message: 'QR Scanner must have a custom code or at least one shared code',
    }))
  }

  return issues
}

/**
 * Validate a Numeric Input (Numpad) node
 */
function validateNumericInput(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.numpadConfig

  // Must have at least one accepted number
  if (!config?.acceptedNumbers || config.acceptedNumbers.length === 0) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_NUMPAD_CODE,
      message: 'Number Pad must have at least one accepted number code',
    }))
  }

  return issues
}

/**
 * Validates all challenge nodes in the workflow
 */
export function validateChallengeNodes(nodes: WorkflowNode[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  for (const node of nodes) {
    switch (node.data.nodeType) {
      case WorkflowNodeType.QuestionAnswer:
        issues.push(...validateQuestionAnswer(node))
        break
      case WorkflowNodeType.QRCode:
        issues.push(...validateQRCode(node))
        break
      case WorkflowNodeType.NumericInput:
        issues.push(...validateNumericInput(node))
        break
      case WorkflowNodeType.ModeratorApproval:
        // Skip validation as per requirements
        break
    }
  }

  return issues
}
